import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { Lock, Shield, ArrowRight, AlertCircle, KeyRound, Smartphone } from "lucide-react";
import { adminLogin, adminVerify2FA, getAdminToken, setAdminToken, adminMe } from "@/lib/api";

// Stages: credentials -> totp_setup (first time, QR) -> totp_verify (returning)
const AdminLoginPage = () => {
  const navigate = useNavigate();
  const [stage, setStage] = useState("credentials");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [provisioningUri, setProvisioningUri] = useState("");
  const [secret, setSecret] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // If the admin is already authenticated (e.g., landed here via browser
  // back button from /admin), validate the token and bounce straight back
  // to the console — no need to type credentials + 2FA again every time.
  useEffect(() => {
    if (!getAdminToken()) return;
    let cancelled = false;
    adminMe()
      .then(() => { if (!cancelled) navigate("/admin", { replace: true }); })
      .catch(() => { setAdminToken(null); /* stale/invalid → stay on login */ });
    return () => { cancelled = true; };
  }, [navigate]);

  const fmtError = (e) => {
    const d = e?.response?.data?.detail;
    if (typeof d === "string") return d;
    return e?.message || "Something went wrong.";
  };

  const submitCredentials = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await adminLogin(email.trim(), password);
      if (res.needs_2fa_setup) {
        setProvisioningUri(res.provisioning_uri);
        setSecret(res.secret);
        setStage("totp_setup");
      } else if (res.needs_2fa) {
        setStage("totp_verify");
      }
    } catch (e) {
      setError(fmtError(e));
    } finally {
      setLoading(false);
    }
  };

  const submitCode = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await adminVerify2FA(email.trim(), password, code.replace(/\s+/g, ""));
      setAdminToken(res.token);
      // `replace: true` so /admin/login doesn't stay in history — browser
      // back from /admin should NOT bounce back to the login screen.
      navigate("/admin", { replace: true });
    } catch (e) {
      setError(fmtError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-[calc(100vh-72px)] bg-[#F8FAFC] flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md bg-white border border-[#E2E8F0] rounded-2xl p-8 shadow-sm" data-testid="admin-login-card">
        <div className="flex items-center gap-2 mb-1">
          <div className="w-10 h-10 rounded-xl bg-[#EFF6FF] flex items-center justify-center">
            <Shield size={20} className="text-[#2563EB]" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#94A3B8] font-semibold">HorseAppCompare</div>
            <h1 className="font-bold text-xl text-[#0F172A]">Admin Console</h1>
          </div>
        </div>
        <p className="text-sm text-[#64748B] mb-6">
          {stage === "credentials" && "Sign in with your admin email and password."}
          {stage === "totp_setup" && "Scan the QR code with Google Authenticator (or any TOTP app), then enter the 6-digit code below."}
          {stage === "totp_verify" && "Enter the 6-digit code from your authenticator app."}
        </p>

        {error && (
          <div className="mb-4 flex items-start gap-2 bg-[#FEF2F2] border border-[#FCA5A5] text-[#B91C1C] text-sm rounded-lg px-3 py-2" data-testid="admin-login-error">
            <AlertCircle size={16} className="shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {stage === "credentials" && (
          <form onSubmit={submitCredentials} className="space-y-4">
            <div>
              <label className="overline block mb-1 text-[#64748B]">Email</label>
              <div className="relative">
                <KeyRound size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                  className="hac-input pl-9"
                  placeholder="admin@horseappcompare.com"
                  data-testid="admin-login-email"
                />
              </div>
            </div>
            <div>
              <label className="overline block mb-1 text-[#64748B]">Password</label>
              <div className="relative">
                <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="hac-input pl-9"
                  placeholder="••••••••"
                  data-testid="admin-login-password"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="hac-btn-primary w-full justify-center disabled:opacity-60"
              data-testid="admin-login-submit"
            >
              {loading ? "Checking…" : (<>Continue <ArrowRight size={14} /></>)}
            </button>
          </form>
        )}

        {(stage === "totp_setup" || stage === "totp_verify") && (
          <form onSubmit={submitCode} className="space-y-4">
            {stage === "totp_setup" && (
              <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl p-4">
                <div className="flex items-center gap-2 mb-3 text-[#0F172A] font-semibold text-sm">
                  <Smartphone size={14} /> Scan with Google Authenticator
                </div>
                <div className="flex justify-center bg-white p-3 rounded-lg border border-[#E2E8F0]">
                  <QRCodeSVG value={provisioningUri} size={180} level="M" data-testid="admin-totp-qr" />
                </div>
                <p className="text-[11px] text-[#64748B] mt-3 break-all">
                  Or enter this secret manually:
                  <code className="ml-1 font-mono text-[#0F172A] bg-white px-1.5 py-0.5 rounded border border-[#E2E8F0]" data-testid="admin-totp-secret">
                    {secret}
                  </code>
                </p>
              </div>
            )}
            <div>
              <label className="overline block mb-1 text-[#64748B]">6-digit code</label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9 ]*"
                maxLength={7}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                required
                autoFocus
                className="hac-input tracking-[0.5em] text-center font-mono text-lg"
                placeholder="••••••"
                data-testid="admin-login-code"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="hac-btn-primary w-full justify-center disabled:opacity-60"
              data-testid="admin-login-verify"
            >
              {loading ? "Verifying…" : (<>Verify & sign in <ArrowRight size={14} /></>)}
            </button>
            <button
              type="button"
              onClick={() => { setStage("credentials"); setCode(""); setError(""); }}
              className="text-xs text-[#64748B] hover:text-[#2563EB] w-full text-center"
            >
              Back to login
            </button>
          </form>
        )}
      </div>
    </main>
  );
};

export default AdminLoginPage;
